import { effect, untracked } from '@preact/signals-core';

import { type DataViewI18nKey, dataViewI18nT } from '../../../core/i18n.js';
import { sortTraitKey } from '../../../core/sort/manager.js';
import { expandCalendarOccurrences } from '../calendar-domain.js';
import type { CalendarSingleView } from '../calendar-view-manager.js';
import type { CalendarIcsSubscription } from '../define.js';
import {
  createCalendarEventRange,
  displayCalendarId,
  readCalendarEvent,
  readCalendarProjectedEvents,
  writeCalendarCellValue,
} from './calendar-event-store.js';
import type {
  CalendarEvent,
  CalendarSubscriptionState,
} from './calendar-view-types.js';
import {
  addCalendarIcsSubscriptionFromUrl,
  type CalendarIcsActionsContext,
  copyCalendarSubscriptionUrl,
  ensureCalendarSubscriptionEventsLoaded,
  importCalendarIcsFileData,
  refreshCalendarIcsSubscription,
  removeCalendarIcsSubscription,
  setCalendarIcsSubscriptionEnabled,
} from './ics/calendar-ics-actions.js';

export class CalendarCommandError extends Error {
  constructor(
    readonly code:
      | 'no-source'
      | 'readonly'
      | 'unavailable'
      | 'offline'
      | 'invalid-ics'
      | 'feed-failed'
      | 'operation-failed',
    readonly messageKey: DataViewI18nKey
  ) {
    super(dataViewI18nT(messageKey));
  }
}
export interface CalendarSourceCommandSession {
  readonly view: CalendarSingleView;
  createEvent(input: {
    day: Date;
    startMinute: number;
    endMinute: number;
    allDay?: boolean;
  }): Promise<{ rowId: string }>;
  renameEvent(rowId: string, title: string): Promise<void>;
  /** The complete events of a range, for a file that leaves the app. */
  exportEvents(range: {
    start: Date;
    end: Date;
  }): Promise<readonly CalendarEvent[]>;
  /**
   * The events a calendar shows: every row this device holds, also while
   * its own changes are still on their way to the server.
   */
  readEvents(range: {
    start: Date;
    end: Date;
  }): Promise<readonly CalendarEvent[]>;
  importIcs(file: File): Promise<void>;
  copyFeedLink(): Promise<void>;
  addFeed(url: string): Promise<void>;
  refreshFeed(id: string): Promise<void>;
  removeFeed(id: string): void;
  setFeedEnabled(id: string, enabled: boolean): void;
  getFeedSnapshot(): {
    subscriptions: readonly CalendarIcsSubscription[];
    events: readonly CalendarEvent[];
    states: Readonly<Record<string, CalendarSubscriptionState>>;
  };
  subscribe(onChange: () => void): () => void;
  dispose(): void;
}

/** One source view, one lifetime. Commands never consult a later UI selection. */
export class CalendarCommandSession implements CalendarSourceCommandSession {
  readonly view: CalendarSingleView;
  private readonly abort = new AbortController();
  private readonly listeners = new Set<() => void>();
  private readonly context: CalendarIcsActionsContext;
  private readonly stop: () => void;
  private readonly stopPresentation: () => void;
  private readonly stopObserving: (() => void) | undefined;
  private stopSourceRows: (() => void) | undefined;
  private sourceRowsRange = '';
  private readonly createdRowIds: string[] = [];
  private subscriptions: Readonly<Record<string, CalendarEvent[]>> = {};
  private states: Readonly<Record<string, CalendarSubscriptionState>> = {};
  private loadKey = '';
  private error: string | undefined;

  constructor(
    private readonly options: {
      view: CalendarSingleView;
      release: () => void;
      onCreated: (event: { rowId: string; startAt: Date }) => Promise<void>;
      canEdit: () => boolean;
      onRenamed?: (previousEvent: CalendarEvent) => Promise<void>;
      context?: CalendarIcsActionsContext;
      observeChanges?: (onChange: () => void) => () => void;
    }
  ) {
    this.view = options.view;
    const ownContext: CalendarIcsActionsContext = {
      view: this.view,
      signal: this.abort.signal,
      readonly: () => !options.canEdit() || this.view.readonly$.value,
      addRow: () => this.view.rowAdd('end'),
      setCellJson: (row, column, value) => {
        writeCalendarCellValue(this.view, row, column, value);
      },
      closeFeedForm: () => {},
      getSubscriptionEvents: () => this.subscriptions,
      getSubscriptionStates: () => this.states,
      getSubscriptionLoadKey: () => this.loadKey,
      setSubscriptionEvents: value => {
        this.subscriptions = value;
        this.notify();
      },
      setSubscriptionStates: value => {
        this.states = value;
        this.notify();
      },
      setSubscriptionLoadKey: value => {
        this.loadKey = value;
      },
      setCalendarError: value => {
        this.error = value;
        this.notify();
      },
      requestCalendarUpdate: () => this.notify(),
      queueIntrinsicContentHeightChange: () => {},
    };
    const context = options.context ?? ownContext;
    this.context = {
      ...context,
      signal: this.abort.signal,
      setSubscriptionEvents: value => {
        context.setSubscriptionEvents(value);
        this.notify();
      },
      setSubscriptionStates: value => {
        context.setSubscriptionStates(value);
        this.notify();
      },
      setCalendarError: value => {
        this.error = value;
        if (!this.abort.signal.aborted) context.setCalendarError(value);
      },
    };
    this.stop = effect(() => {
      const subscriptions = this.view.calendarIcsSubscriptions$.value;
      ensureCalendarSubscriptionEventsLoaded(this.context, subscriptions);
    });
    this.stopPresentation = effect(() => {
      void this.view.search$.value;
      void this.view.filter$.value;
      void this.view.visibleCalendarIds$.value;
      void this.view.traitGet(sortTraitKey)!.sortList$.value;
      untracked(() => this.notify());
    });
    this.stopObserving = options.observeChanges?.(() => this.notify());
  }
  private notify(): void {
    if (!this.abort.signal.aborted)
      this.listeners.forEach(listener => listener());
  }
  private assert(writing = false): void {
    if (this.abort.signal.aborted)
      throw new CalendarCommandError(
        'unavailable',
        'calendar.chrome.unavailable'
      );
    if (writing && (!this.options.canEdit() || this.view.readonly$.value))
      throw new CalendarCommandError('readonly', 'calendar.chrome.readonly');
  }
  private assertOnline(): void {
    if (typeof navigator !== 'undefined' && navigator.onLine === false)
      throw new CalendarCommandError('offline', 'calendar.chrome.offline');
  }
  private checkError(
    code: CalendarCommandError['code'],
    key: DataViewI18nKey
  ): void {
    if (this.error) throw new CalendarCommandError(code, key);
  }
  async createEvent(input: {
    day: Date;
    startMinute: number;
    endMinute: number;
    allDay?: boolean;
  }): Promise<{ rowId: string }> {
    this.assert(true);
    const event = await createCalendarEventRange(
      this.view,
      input.day,
      input.startMinute,
      input.endMinute,
      { allDay: input.allDay }
    );
    if (!event)
      throw new CalendarCommandError(
        'operation-failed',
        'calendar.chrome.operationFailed'
      );
    await this.options.onCreated(event);
    this.assert();
    this.createdRowIds.push(event.rowId);
    this.notify();
    return { rowId: event.rowId };
  }
  async renameEvent(rowId: string, title: string): Promise<void> {
    this.assert(true);
    const previousEvent = readCalendarEvent(this.view, rowId, {
      includeDetails: true,
    });
    if (!previousEvent || previousEvent.externalReadonly)
      throw new CalendarCommandError(
        'unavailable',
        'calendar.chrome.unavailable'
      );
    const nextTitle = title.trim() || dataViewI18nT('calendar.event.untitled');
    if (nextTitle === previousEvent.title) return;
    this.context.setCellJson(
      rowId,
      this.view.mainProperties$.value.titleColumn,
      nextTitle
    );
    this.notify();
    await this.options.onRenamed?.(previousEvent);
    this.assert();
  }
  async exportEvents(range: {
    start: Date;
    end: Date;
  }): Promise<readonly CalendarEvent[]> {
    return this.sourceEvents(range, false);
  }
  async readEvents(range: {
    start: Date;
    end: Date;
  }): Promise<readonly CalendarEvent[]> {
    return this.sourceEvents(range, true);
  }
  private sourceEvents(
    range: { start: Date; end: Date },
    local: boolean
  ): readonly CalendarEvent[] {
    this.assert();
    this.watchSourceRows(range);
    const events = readCalendarSourceEvents(
      this.view,
      range,
      false,
      local,
      local ? this.createdRowIds : []
    );
    const visible = new Set(this.view.visibleCalendarIds$.value);
    const feeds = this.getFeedSnapshot().events.filter(
      event => !visible.size || visible.has(displayCalendarId(event.calendarId))
    );
    return [...events, ...feeds].filter(
      event =>
        expandCalendarOccurrences(event, range.start, range.end).length > 0
    );
  }
  async importIcs(file: File): Promise<void> {
    this.assert(true);
    this.error = undefined;
    await importCalendarIcsFileData(this.context, file);
    this.assert();
    this.checkError('invalid-ics', 'calendar.error.importFailed');
  }
  async copyFeedLink(): Promise<void> {
    this.assertOnline();
    this.assert(true);
    this.error = undefined;
    await copyCalendarSubscriptionUrl(this.context);
    this.assert();
    this.checkError('operation-failed', 'calendar.error.feedUrlCreateFailed');
  }
  async addFeed(url: string): Promise<void> {
    this.assertOnline();
    this.assert(true);
    this.error = undefined;
    addCalendarIcsSubscriptionFromUrl(this.context, url);
    this.checkError('feed-failed', 'calendar.error.feedInvalid');
  }
  async refreshFeed(id: string): Promise<void> {
    this.assertOnline();
    this.assert();
    const subscription = this.view.calendarIcsSubscriptions$.value.find(
      value => value.id === id
    );
    if (!subscription || subscription.enabled === false)
      throw new CalendarCommandError(
        'unavailable',
        'calendar.chrome.unavailable'
      );
    await refreshCalendarIcsSubscription(this.context, subscription);
    this.assert();
    this.checkError('feed-failed', 'calendar.error.feedFailed');
  }
  removeFeed(id: string): void {
    this.assert(true);
    removeCalendarIcsSubscription(this.context, id);
  }
  setFeedEnabled(id: string, enabled: boolean): void {
    this.assert(true);
    setCalendarIcsSubscriptionEnabled(this.context, id, enabled);
  }
  getFeedSnapshot() {
    return {
      subscriptions: this.view.calendarIcsSubscriptions$.value,
      events: Object.values(this.context.getSubscriptionEvents()).flat(),
      states: this.context.getSubscriptionStates(),
    };
  }
  /**
   * The database answers a first read with "still loading" and delivers the
   * rows afterwards. That delivery changes no document, so it is announced
   * here: whoever read the range reads it again.
   */
  private watchSourceRows(range: { start: Date; end: Date }): void {
    const key = `${range.start.getTime()}:${range.end.getTime()}`;
    if (key === this.sourceRowsRange) return;
    this.stopSourceRows?.();
    this.sourceRowsRange = key;
    let first = true;
    this.stopSourceRows = effect(() => {
      void projectCalendarSourceRows(this.view, range);
      if (first) {
        first = false;
        return;
      }
      untracked(() => this.notify());
    });
  }
  subscribe(onChange: () => void): () => void {
    this.assert();
    this.listeners.add(onChange);
    return () => this.listeners.delete(onChange);
  }
  dispose(): void {
    if (this.abort.signal.aborted) return;
    this.abort.abort();
    this.stop();
    this.stopPresentation();
    this.stopObserving?.();
    this.stopSourceRows?.();
    this.listeners.clear();
    this.subscriptions = {};
    this.states = {};
    this.options.release();
  }
}

/** The rows of a calendar source in a range; undefined without a start date column. */
function projectCalendarSourceRows(
  view: CalendarSingleView,
  range: { start: Date; end: Date }
) {
  const start = view.startDateCol$.value;
  if (!start) return undefined;
  const overlap = {
    type: 'temporal-overlap' as const,
    startPropertyId: start,
    endPropertyId: view.endDateCol$.value,
    windowStart: range.start.getTime(),
    windowEnd: range.end.getTime(),
  };
  const recurrence = view.recurrenceCol$.value;
  return view.projectRows({
    scope: 'temporal-window',
    viewId: view.id,
    viewMode: view.type,
    predicates: [
      recurrence
        ? {
            type: 'predicate-group',
            operator: 'or',
            predicates: [
              overlap,
              {
                type: 'column',
                operator: 'is-not-empty',
                propertyId: recurrence,
              },
            ],
          }
        : overlap,
    ],
  });
}

/** Full domain data from the existing filtered/sorted source projection, including recurring masters. */
export function readCalendarSourceEvents(
  view: CalendarSingleView,
  range: { start: Date; end: Date },
  includeHidden = false,
  /** Accept the rows of this device before the server has confirmed them. */
  local = false,
  createdRowIds: readonly string[] = []
): CalendarEvent[] {
  const projection = projectCalendarSourceRows(view, range);
  if (!projection)
    throw new CalendarCommandError(
      'unavailable',
      'calendar.chrome.unavailable'
    );
  if (
    !projection.complete &&
    (!local || (projection.syncState === 'syncing' && !createdRowIds.length))
  )
    throw new CalendarCommandError(
      'unavailable',
      'calendar.notice.partialProjection'
    );
  const visible = new Set(view.visibleCalendarIds$.value);
  return readCalendarProjectedEvents(view, projection, createdRowIds, {
    includeDetails: true,
  }).flatMap(event => {
    return event &&
      (includeHidden ||
        !visible.size ||
        visible.has(displayCalendarId(event.calendarId)))
      ? [event]
      : [];
  });
}
